import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PatchEducationHistoryCoursesResponseBody } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey } from '~/utils';
import { useRequest } from '~/lib/useRequest';

export type UpdateEducationHistoryMetadataParams = {
  studentId: string;
  /** undefined when the EH item has no student-documents reference (e.g. school-created without file) */
  educationHistoryId: string | undefined;
  /** Only the fields that actually changed, keyed by student documents API field name */
  metadata: Record<string, string | undefined>;
  applicationId: string;
  /** The education history item key on the application (used to update the application resource) */
  educationHistoryKey: string;
};

/**
 * Fields from the student documents API metadata that also live on the application resource.
 * Maps student-docs field name → application field name.
 */
const STUDENT_DOCS_TO_APPLICATION_FIELD: Record<string, string> = {
  schoolName: 'schoolName',
  educationLevel: 'educationLevel',
  issuingCountry: 'educationCountry',
  credentialReceived: 'credentialReceived',
  startDate: 'startDate',
  endDate: 'endDate',
};

/**
 * Extracts the subset of changed metadata that should be synced to the application resource,
 * mapping from student documents field names to the application's field names.
 */
function buildApplicationEducationHistory(
  metadata: Record<string, string | undefined>
): Record<string, string | undefined> | null {
  const applicationFields: Record<string, string | undefined> = {};
  let hasFields = false;

  const DATE_FIELDS = new Set(['startDate', 'endDate']);

  for (const [studentDocsKey, value] of Object.entries(metadata)) {
    const applicationKey = STUDENT_DOCS_TO_APPLICATION_FIELD[studentDocsKey];
    if (applicationKey) {
      // The application API expects YYYY-MM-DD; pad month-granularity dates with -01
      applicationFields[applicationKey] =
        value && DATE_FIELDS.has(applicationKey) ? value.substring(0, 7) + '-01' : value;
      hasFields = true;
    }
  }

  return hasFields ? applicationFields : null;
}

/**
 * Mutation hook for updating education history metadata fields.
 * Updates both the student documents API and the application API
 * when application-relevant fields change (schoolName, educationLevel,
 * educationCountry, credentialReceived, startDate, endDate).
 */
export function useUpdateEducationHistoryMetadata() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation({
    mutationKey: ['updateEducationHistoryMetadata'],
    mutationFn: async (params: UpdateEducationHistoryMetadataParams) => {
      let studentDocsResponse: PatchEducationHistoryCoursesResponseBody | undefined;

      // Only call student documents API when the item has a document reference
      if (params.educationHistoryId) {
        studentDocsResponse = await request<PatchEducationHistoryCoursesResponseBody>(
          `${config.apiHost}/private/v1/students/${params.studentId}/education-history/${params.educationHistoryId}`,
          {
            method: 'PATCH',
            body: {
              data: {
                type: 'education-history',
                attributes: {
                  metadata: params.metadata,
                },
              },
            },
            isExpectedResponse: isPatchExpectedResponse,
          }
        );
      }

      const applicationFields = buildApplicationEducationHistory(params.metadata);
      if (applicationFields) {
        await request(`${config.apiHost}/private/v1/applications/${params.applicationId}`, {
          method: 'PATCH',
          body: {
            data: {
              id: params.applicationId,
              type: 'application',
              attributes: {
                educationHistory: {
                  [params.educationHistoryKey]: applicationFields,
                },
              },
            },
          },
          isExpectedResponse: isApplicationPatchExpectedResponse,
        });
      }

      return studentDocsResponse?.data;
    },
    onSuccess: async (_data, variables) => {
      const invalidations: Promise<void>[] = [
        queryClient.invalidateQueries({ queryKey: ['application', variables.applicationId] }),
      ];
      if (variables.educationHistoryId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: ['entity-history', 'educationHistory', variables.educationHistoryId],
          })
        );
      }
      await Promise.all(invalidations);
    },
  });

  return {
    updateEducationHistoryMetadata: mutateAsync,
    isUpdatingEducationHistoryMetadata: isLoading,
    updateEducationHistoryMetadataError: error,
  };
}

function isPatchExpectedResponse(res: unknown): res is PatchEducationHistoryCoursesResponseBody {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}

function isApplicationPatchExpectedResponse(res: unknown): res is { data: { type: string; id: string } } {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
